// Thin fetch wrapper for the portal API (same origin, session cookie + CSRF header).

export class ApiError extends Error {
  status: number
  code: string
  errors?: unknown

  constructor(status: number, message: string, code = 'error', errors?: unknown) {
    super(message)
    this.status = status
    this.code = code
    this.errors = errors
  }

  /** Field → messages, or a list of messages (e.g. publish problems), flattened for display. */
  get messages(): string[] {
    const out: string[] = []
    const walk = (value: unknown) => {
      if (typeof value === 'string') out.push(value)
      else if (Array.isArray(value)) value.forEach(walk)
      else if (value && typeof value === 'object') Object.values(value).forEach(walk)
    }
    walk(this.errors)
    return out.length ? [...new Set(out)] : [this.message]
  }
}

const csrfToken = () => document.cookie.match(/(?:^|;\s*)portal_csrftoken=([^;]+)/)?.[1] ?? ''

type Body = Record<string, unknown> | unknown[] | FormData | undefined

async function request<T>(method: string, path: string, body?: Body, keepalive = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (method !== 'GET') headers['X-CSRFToken'] = decodeURIComponent(csrfToken())
  let payload: BodyInit | undefined
  if (body instanceof FormData) payload = body
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }
  let response: Response
  try {
    response = await fetch(`/api${path}`, { method, headers, body: payload, credentials: 'same-origin', keepalive })
  } catch {
    throw new ApiError(0, 'The portal could not be reached. Check your connection and try again.', 'network')
  }
  if (response.status === 204) return undefined as T
  const data = await response.json().catch(() => null)
  if (!response.ok) {
    // Session expired: go sign in (and come back). Never settle, so no error UI flashes during the redirect.
    if (response.status === 401 && onUnauthorized()) return new Promise<T>(() => {})
    throw new ApiError(response.status, data?.detail ?? `Request failed (${response.status})`, data?.code, data?.errors)
  }
  return data as T
}

/** A signed-out API call sends the user to sign in, then back to the page they were on. */
function onUnauthorized() {
  if (window.location.pathname.startsWith('/login')) return false
  window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)
  return true
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: Body) => request<T>('POST', path, body),
  put: <T>(path: string, body?: Body) => request<T>('PUT', path, body),
  putKeepalive: <T>(path: string, body?: Body) => request<T>('PUT', path, body, true),
  patch: <T>(path: string, body?: Body) => request<T>('PATCH', path, body),
  delete: (path: string) => request<void>('DELETE', path),
}

/** Stream server-sent chat events from a POST request (EventSource cannot POST the question). */
export async function streamChat(
  message: string,
  history: { role: 'user' | 'assistant'; content: string }[],
  onEvent: (event: string, data: any) => void,
  signal?: AbortSignal,
) {
  let response: Response
  try {
    response = await fetch('/api/chat', {
      method: 'POST', credentials: 'same-origin', signal,
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream, application/json', 'X-CSRFToken': decodeURIComponent(csrfToken()) },
      body: JSON.stringify({ message, history }),
    })
  } catch (error) {
    if (signal?.aborted) throw error
    throw new ApiError(0, 'The portal could not be reached. Check your connection and try again.', 'network')
  }
  if (!response.ok) {
    const data = await response.json().catch(() => null)
    if (response.status === 401 && onUnauthorized()) return new Promise<void>(() => {})
    throw new ApiError(response.status, data?.detail ?? `Request failed (${response.status})`, data?.code)
  }
  if (!response.body) throw new ApiError(0, 'Streaming is not available in this browser.')
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { value, done } = await reader.read()
    buffer = (buffer + decoder.decode(value, { stream: !done })).replace(/\r\n/g, '\n')
    let boundary = buffer.indexOf('\n\n')
    while (boundary >= 0) {
      const frame = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const type = frame.match(/^event: (.+)$/m)?.[1]
      const data = frame.match(/^data: (.+)$/m)?.[1]
      if (type && data) onEvent(type, JSON.parse(data))
      boundary = buffer.indexOf('\n\n')
    }
    if (done) break
  }
}

export const errorMessage = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong. Please try again.')
